import { apiClient, PaginatedResponse, QueryParams } from './apiClient';
import type { AdCampaignLocationTarget } from '@/types/ads';

export type BackgroundSlideTargetMode = 'ALL' | 'USERS' | 'LOCATION';

export interface BackgroundSlide {
  id: string;
  imageUrl: string;
  type: string;
  title: string;
  year: string;
  genre?: string;
  duration?: string;
  classification?: string;
  isKidsMode: boolean;
  sportsMode: boolean;
  targetMode?: BackgroundSlideTargetMode;
  targetUserIds?: number[];
  locationTargets?: AdCampaignLocationTarget[];
  active: boolean;
  displayOrder?: number;
  createdDate?: string;
  updatedDate?: string;
}

export interface CreateBackgroundSlideRequest {
  imageUrl: string;
  type: string;
  title: string;
  year: string;
  genre?: string;
  duration?: string;
  classification?: string;
  isKidsMode: boolean;
  active?: boolean;
  displayOrder?: number;
  sportsMode: boolean;
  targetMode?: BackgroundSlideTargetMode;
  targetUserIds?: number[];
  locationTargets?: AdCampaignLocationTarget[];
}

export interface BackgroundSlideResponse {
  success: boolean;
  message: string;
  slide: BackgroundSlide;
}

export interface BackgroundSlidesQueryParams extends QueryParams {
  active?: boolean;
  isKidsMode?: boolean;
}

export const backgroundSlideService = {
  /**
   * Get background slides with pagination and filtering
   */
  async getBackgroundSlides(params: BackgroundSlidesQueryParams = {}): Promise<PaginatedResponse<BackgroundSlide>> {
    // Map _page to page and _size to size for the API
    const apiParams: QueryParams = { ...params };
    return await apiClient.get<{
      content: BackgroundSlide[];
      totalElements: number;
      size: number;
      page: number;
      totalPages: number;
    }>('/background-slides', apiParams);
  },

  /**
   * Get a single background slide by ID
   */
  async getBackgroundSlideById(id: string): Promise<BackgroundSlide> {
    const response = await apiClient.get<{ success: boolean; slide: BackgroundSlide }>(`/background-slides/${id}`);
    return response.slide;
  },

  /**
   * Create a new background slide
   */
  async createBackgroundSlide(slideData: CreateBackgroundSlideRequest): Promise<BackgroundSlideResponse> {
    return apiClient.create<CreateBackgroundSlideRequest, BackgroundSlideResponse>('/background-slides', slideData);
  },

  /**
   * Update a background slide
   */
  async updateBackgroundSlide(id: string, slideData: Partial<CreateBackgroundSlideRequest>): Promise<BackgroundSlideResponse> {
    return apiClient.update<Partial<CreateBackgroundSlideRequest>, BackgroundSlideResponse>('/background-slides', id, slideData);
  },

  /**
   * Delete a background slide
   */
  async deleteBackgroundSlide(id: string): Promise<{ success: boolean; message: string }> {
    return apiClient.deleteById<{ success: boolean; message: string }>('/background-slides', id);
  },

  /**
   * Update display order for a background slide
   */
  async updateDisplayOrder(id: string, displayOrder: number): Promise<BackgroundSlideResponse> {
    return apiClient.patch<BackgroundSlideResponse>(`/background-slides/${id}/display-order`, { displayOrder });
  },
};
